import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

// Bottom-right on desktop, top on mobile, maximum three (Build Book §09).
// Success dismisses after 4s; errors NEVER auto-dismiss and carry a copyable
// request_id — an error that vanishes before it is read is an error that gets
// reported as "it just didn't work".

const ToastContext = createContext(null)
const MAX_TOASTS = 3
const SUCCESS_MS = 4000

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const nextId = useRef(1)

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message, { tone = 'info', requestId = null } = {}) => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, message, tone, requestId }].slice(-MAX_TOASTS))
      if (tone !== 'error') {
        setTimeout(() => dismiss(id), SUCCESS_MS)
      }
      return id
    },
    [dismiss],
  )

  const value = useMemo(
    () => ({
      show,
      dismiss,
      // For a screen whose retry just succeeded: an error about the attempt
      // before it is no longer true, and left up it reads as the current
      // state (the register page showed "already registered" under a
      // registration that had just gone through).
      clear: () => setToasts([]),
      success: (m) => show(m, { tone: 'success' }),
      // Errors take the whole envelope, so the reference travels with the
      // message without every call site remembering to pass it.
      error: (m, requestId) => show(m, { tone: 'error', requestId }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="ui-toasts" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`ui-toast ui-toast-${t.tone}`}
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            <span>{t.message}</span>
            {t.requestId ? <span className="ui-toast-ref">Ref {t.requestId}</span> : null}
            {t.tone === 'error' ? (
              <button type="button" className="ui-toast-close" onClick={() => dismiss(t.id)}>
                Dismiss
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast() outside <ToastProvider>')
  return context
}

/**
 * On a 400: message as a toast AND fields under the inputs — never only a
 * toast, never only inline. Focus and scroll to the first errored field.
 */
export function applyApiError(error, { toast, setError, focusFirst = true }) {
  const fields = error?.fields || {}
  const names = Object.keys(fields)

  for (const name of names) {
    setError?.(name, { type: 'server', message: fields[name] })
  }

  toast.error(error?.message || 'Something went wrong.', error?.requestId)

  if (focusFirst && names.length) {
    const first = document.querySelector(`[name="${CSS.escape(names[0])}"]`)
    first?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    first?.focus?.()
  }
}
